import React, { Component } from 'react';
import { bool, func, object } from 'prop-types';
import { find, propEq } from 'ramda';

// Redux
import { connect } from 'react-redux';
import {hideBackButton, setHeaderTitle, showBackButton} from '../../../actions/header/headerActions';

// Components
import ActionsPanel from '../../ActionsPanel/ActionsPanel';
import HealthLandingPage from './HealthLandingPage';
import Layout from '../../Layout/Layout';
import ContentPanel from "../../ContentPanel/ContentPanel";
import MedicationsLandingPage from './MedicationsLandingPage/MedicationsLandingPage';
import SecondaryMenuItem from '../../SecondaryMenu/SecondaryMenuItem';
import SecondaryMenu from '../../SecondaryMenu/SecondaryMenu';
import LoadingState from "../../LoadingState";

// Styles
import s from './RootContainer.css';
import cx from 'classnames';

// The RootContainers in this page act as a state switcher to swap out different LandingPages
export class RootContainer extends Component {
  // There are router level props here that are omitted as they are not used
  static propTypes = {
    isCollapsed: bool,
    authenticatedPreferences: object,
    header: object,
    setHeaderTitle: func,
    hideHeaderBackButton: func,
    showHeaderBackButton: func,
  };

  static defaultProps = {
    isCollapsed: true,
  };

  constructor(props) {
    super(props);

    let menuKey;
    if (Object.keys(props.authenticatedPreferences).length > 0) {
      menuKey = this.getActiveMenuKey();
    }

    this.state = {
      menuItems: [
        {key: 'medications', name: 'My Medications', landingPage: <MedicationsLandingPage/>},
        {key: 'records', name: 'My Records', landingPage: <HealthLandingPage/>},
      ],
      activeMenuKey: menuKey,
      windowWidth: null,
      expanded: null,
    };

    this.isUnmounted = false;

  }

  handleChildLandingPageVisibilityChange = (childData) => {
    if (childData) {
      this.setState({
        expanded: !this.state.expanded,
      })
    }
  }

  componentWillMount() {
    this.props.setHeaderTitle('Health');

    this.setState({
      expanded: !this.props.isCollapsed,
      windowWidth: window.innerWidth,
    });
  };

  componentDidUpdate(previousProps) {
    // On refresh of the Health Record page the `authenticatedPreferences` are undefined, we need to wait until they're available
    // before deciding what is our active menu key. Otherwise, the value will default to 'medications'. This is problematic
    // because not all accounts have medications enabled. Therefore, that page will invoke the server which will reject the request.
    if (Object.keys(this.props.authenticatedPreferences).length > 0 && Object.keys(previousProps.authenticatedPreferences).length === 0) {
      const menuKey = this.getActiveMenuKey();
      this.setState({ activeMenuKey: menuKey})
    }
  }

  // Listen to window and resize event
  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  // On unmount, remove listeners to prevent memory leaks
  componentWillUnmount() {
    this.isUnmounted = true;
    window.removeEventListener('resize', this.handleResize);
  }

  getActiveMenuKey = () => {
    return this.props.authenticatedPreferences.recordsEnabled
        ? 'records'
        : 'medications';
  }

  handleActiveMenuItem = (menuKey) => {
    this.setState({
      activeMenuKey: menuKey,
    });

    if (this.state.windowWidth <= 768) {
      this.setState({
        expanded: !this.state.expanded,
      });
    }
  };

  handleResize = () => {
    const windowWidth = this.state.windowWidth;
    const mobileContentPanelOpen = this.state.expanded;
    const isDesktop = (windowWidth >= 768 && mobileContentPanelOpen);

    if (!this.isUnmounted) {
      let updatedState = { windowWidth: window.innerWidth };
      if (isDesktop) {
        updatedState.expanded = !mobileContentPanelOpen;
      }

      this.setState(updatedState);
    }
  };

  renderSecondaryMenu = () => {
    return (
        <SecondaryMenu id="health_page_secondary_menu" className={s.secondaryMenu}>
          {this.state.menuItems.map(menuItem => (
              <SecondaryMenuItem
                  active={menuItem.key === this.state.activeMenuKey}
                  className={s.menuItem}
                  id={`menu_item_${menuItem.key}`}
                  key={menuItem.key}
                  handleClick={() => this.handleActiveMenuItem(menuItem.key)}
              >
                <h1 className={s.menuTitle}>{menuItem.name}</h1>
              </SecondaryMenuItem>
          ))}
        </SecondaryMenu>
    )
  }

  renderSecondaryNavAreas = (recordsEnabled, medicationRefillsEnabled, isSecondaryMenuRendered) => {
      const area = this.state.activeMenuKey;
      switch (area) {
          case 'medications':
              if(medicationRefillsEnabled) {
                //Look up the landing page associated with the key "medications" and return it
                return <MedicationsLandingPage hasParentSecondaryMenu ={isSecondaryMenuRendered} parentCallback={this.handleChildLandingPageVisibilityChange} />
              }
              break;
          case 'records':
              if(recordsEnabled) {
                //Look up the landing page associated with the key "records" and return it
                return <HealthLandingPage hasParentSecondaryMenu ={isSecondaryMenuRendered} parentCallback={this.handleChildLandingPageVisibilityChange} />
              }
              break;
          default:
              //We either don't have a valid key or the key leads to a disabled page, either way we don't want to show anything
              return (
                  <Layout>
                    <p className={s.messagePlaceholder}>
                      This functionality is not available at this time.
                    </p>
                  </Layout>
              );
      }
      return find(propEq('key', this.state.activeMenuKey))(this.state.menuItems)['landingPage'];
    };

  // @TODO Update ContentPanel to use SlidingPanel
  render = () => {
    // We wont know out the gate which secondary panel will be shown so we need to wait until
    // we have calculated the menu key before not showing loading.
    if (!this.state.activeMenuKey) {
      return (
        <Layout>
          <LoadingState context="health"/>
        </Layout>
      );
    }

    const recordsEnabled = this.props.authenticatedPreferences.recordsEnabled;
    const medicationRefillsEnabled = this.props.authenticatedPreferences.medicationRefillsEnabled;

    const mobileContentPanelIsOpen = this.state.expanded;
    const mobileContentPanelStyle = (mobileContentPanelIsOpen ? s.isActive : '');

    //If both preferences are true we need to display a secondary menu
    if (recordsEnabled && medicationRefillsEnabled) {
      return (
        <Layout visibleXScroll>
          <ActionsPanel className={cx(s.actionsPanel)}>
            {this.renderSecondaryMenu()}
          </ActionsPanel>
          <ContentPanel className={cx(s.contentPanel, mobileContentPanelStyle)}>
            {this.renderSecondaryNavAreas(recordsEnabled,medicationRefillsEnabled, true)}
          </ContentPanel>
        </Layout>
      );
    }
    // If a single preference is true, and all others are false, we should not display a SecondaryMenu
    else {
      return (
        <Layout>
          {this.renderSecondaryNavAreas(recordsEnabled,medicationRefillsEnabled, false)}
        </Layout>
      );
    }
  }
}

const mapStateToProps = (state) => {
  return {
    authenticatedPreferences: state.authenticatedPreferences,
    header: state.header,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setHeaderTitle: title => dispatch(setHeaderTitle(title)),
  showHeaderBackButton: () => {
    dispatch(showBackButton())
  },
  hideHeaderBackButton: () => {
    dispatch(hideBackButton())
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RootContainer);



// WEBPACK FOOTER //
// ./src/components/Pages/Health/RootContainer.js